import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BsPlus, BsCheck } from 'react-icons/bs'
import { FiEdit, FiPlusCircle, FiX, FiTrash2 } from 'react-icons/fi'
import axios from 'axios'
import { Oval } from 'react-loader-spinner'
import { Context } from '../store/Store'
import { updateStockQuantity } from '../store/actions/actions'
import Subtitle from '../components/Subtitle'
import Title from '../components/Title'

export function StocksStatus() {
  const navigate = useNavigate()
  const { app, dispatch } = React.useContext(Context)
  const [loading, setLoading] = React.useState(true)
  const [consolidatedStocks, setConsolidatedStocks] = React.useState([])
  const [apiError, setApiError] = React.useState(false)
  const [editingItem, setEditingItem] = React.useState(null)
  const [editValue, setEditValue] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [availableLegumes, setAvailableLegumes] = React.useState([])

  // Fonction pour naviguer vers le formulaire de stock avec le légume pré-sélectionné
  const handleStockAction = (legume, action) => {
    // Stocker temporairement les informations dans localStorage
    if (action === 'update' && legume) {
      localStorage.setItem('stockAction', JSON.stringify({
        action: action,
        legume: {
          id: legume.legume_id,
          nom: legume.legume_nom,
          unite: legume.unite,
          quantite: legume.quantite || 0
        }
      }))
    } else if (action === 'create') {
      localStorage.setItem('stockAction', JSON.stringify({
        action: action,
        legume: null
      }))
    }

    // Rediriger vers le formulaire de stock
    navigate('/stocks/new')
  }

  // Fonction pour activer l'édition en ligne d'un légume
  const startEditing = (item) => {
    setEditingItem(item.legume_id)
    setEditValue(item.quantite)
  }

  // Fonction pour annuler l'édition en ligne
  const cancelEditing = () => {
    setEditingItem(null)
    setEditValue('')
  }

  // Fonction pour soumettre la mise à jour de la quantité
  const submitEdit = (item) => {
    setSubmitting(true)

    // Vérifier si la quantité est valide
    const newQuantity = parseFloat(editValue)
    if (isNaN(newQuantity) || newQuantity < 0) {
      alert('Veuillez entrer une quantité valide')
      setSubmitting(false)
      return
    }

    // Préparer les données pour l'API
    const stockData = {
      legume_id: item.legume_id,
      legume_nom: item.legume_nom,
      quantite: newQuantity,
      unite: item.unite
    }

    // Appeler l'API pour mettre à jour le stock
    submitEditAPI(stockData, item)
  }

  // Fonction pour remettre à zéro la quantité d'un légume (le légume reste dans la liste)
  const deleteLegumeFromStock = (item) => {
    if (window.confirm(`Êtes-vous sûr de vouloir remettre à zéro le stock de ${item.legume_nom} ?`)) {
      setSubmitting(true)

      // Préparer les données pour l'API (quantité à zéro)
      const stockData = {
        producteurId: app.profil && app.profil.id ? app.profil.id : '44624',
        date: new Date().toLocaleDateString('fr-FR'),
        stockItems: [
          {
            legumeId: item.legume_id,
            quantite: 0,
            unite: item.unite
          }
        ]
      }

      // Appeler l'API pour mettre à jour le stock
      axios
        .post(
          `${
            process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
          }/wp-json/bb/v1/stock`,
          stockData
        )
        .then(function (response) {
          if (response.status === 200) {
            // Mettre à jour l'état local (garder le légume mais avec quantité zéro)
            setConsolidatedStocks(prevStocks => {
              return prevStocks.map(stock => {
                if (stock.legume_id === item.legume_id) {
                  return { ...stock, quantite: 0 }
                }
                return stock
              })
            })

            // Mettre à jour le contexte global pour éviter le rechargement complet
            dispatch(updateStockQuantity(item.legume_id, 0))

            // Note: On ne met plus à jour la liste des légumes disponibles
            // car le légume reste visible dans la liste avec quantité zéro

            // Réinitialiser l'état d'édition
            setSubmitting(false)
            setEditingItem(null)
            setEditValue('')
          } else {
            alert('Erreur lors de la mise à jour du stock et de la Google Sheet')
            setSubmitting(false)
          }
        })
        .catch(function (error) {
          console.error('Erreur API:', error)
          alert('Erreur lors de la mise à jour du stock et de la Google Sheet')
          setSubmitting(false)
        })
    }
  }

  // Fonction pour soumettre la mise à jour de la quantité (suite)
  const submitEditAPI = (stockData, item) => {
    // Préparer les données pour l'API dans le format attendu
    const apiData = {
      producteurId: app.profil && app.profil.id ? app.profil.id : '44624',
      date: new Date().toLocaleDateString('fr-FR'),
      stockItems: [
        {
          legumeId: item.legume_id,
          quantite: stockData.quantite,
          unite: stockData.unite
        }
      ]
    }

    axios
      .post(
        `${
          process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
        }/wp-json/bb/v1/stock`,
        apiData
      )
      .then(function (response) {
        if (response.status === 200) {
          // Mettre à jour l'état local
          setConsolidatedStocks(prevStocks => {
            return prevStocks.map(stock => {
              if (stock.legume_id === item.legume_id) {
                // Mettre à jour la quantité (même si c'est zéro, on garde le légume)
                return { ...stock, quantite: stockData.quantite }
              }
              return stock
            })
          })

          // Mettre à jour le contexte global pour éviter le rechargement complet
          dispatch(updateStockQuantity(item.legume_id, stockData.quantite))

          // Réinitialiser l'état d'édition
          setSubmitting(false)
          setEditingItem(null)
          setEditValue('')
        } else {
          alert('Erreur lors de la mise à jour du stock et de la Google Sheet')
          setSubmitting(false)
        }
      })
      .catch(function (error) {
        console.error('Erreur API:', error)
        alert('Erreur lors de la mise à jour du stock et de la Google Sheet')
        setSubmitting(false)
      })
  }

  // Fonction pour obtenir les détails d'un légume à partir de son ID
  const getLegumeDetails = (legumeId) => {
    const legumeDetails = app.legumes?.find(
      (legume) => parseInt(legume.id) === parseInt(legumeId)
    )
    return legumeDetails
  }

  // Fonction pour obtenir la valeur maximale pour un légume
  const getLegumeMax = (legumeId) => {
    const legumeDetails = app.legumes_nomenclature_stock?.find(
      (legume) => parseInt(legume.legume.id) === parseInt(legumeId)
    )

    return legumeDetails?.max || 1000
  }

  // Fonction pour déterminer le niveau de remplissage (peut dépasser 100%)
  const getFillingLevel = (quantity, legumeId) => {
    const max = getLegumeMax(legumeId)
    const level = (parseFloat(quantity) / max) * 100
    return level // Afficher la vraie valeur même si > 100%
  }

  React.useEffect(() => {
    // Charger l'état consolidé des stocks
    axios
      .get(
        `${
          process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
        }/wp-json/bb/v1/stocks/${app.profil && app.profil.id ? app.profil.id : '44624'}`
      )
      .then(function (response) {
        if (response.status === 200) {
          // Traiter la nouvelle structure de données
          if (response.data && response.data.stocks && Array.isArray(response.data.stocks)) {
            // Dictionnaire pour stocker la déclaration la plus récente pour chaque légume
            const latestStockByLegume = {};
            const latestDateByLegume = {};

            // Parcourir tous les stocks (déclarations)
            response.data.stocks.forEach(stock => {
              if (stock.items && Array.isArray(stock.items)) {
                // Convertir la date au format DD/MM/YYYY ou MM/DD/YYYY en objet Date
                const dateParts = stock.date.split('/');

                // Standardiser l'interprétation des dates
                // Nous considérons toujours le format comme étant DD/MM/YYYY pour éviter les ambiguïtés
                // car c'est le format français standard utilisé dans l'application
                const day = parseInt(dateParts[0]);
                const month = parseInt(dateParts[1]) - 1; // Les mois commencent à 0 en JavaScript
                const year = parseInt(dateParts[2]);

                // Créer la date en format DD/MM/YYYY
                const stockDate = new Date(year, month, day);

                // Parcourir tous les items de chaque stock
                stock.items.forEach(item => {
                  const legumeId = item.legume_id.ID;
                  const legumeName = item.legume_nom;
                  const quantity = parseFloat(item.quantite);
                  const unite = item.unite;

                  // Vérifier si c'est la déclaration la plus récente pour ce légume
                  if (!latestDateByLegume[legumeId] || stockDate > latestDateByLegume[legumeId]) {
                    latestDateByLegume[legumeId] = stockDate;
                    latestStockByLegume[legumeId] = {
                      legume_id: legumeId,
                      legume_nom: legumeName,
                      quantite: quantity,
                      unite: unite
                    };
                  }
                });
              }
            });

            // Convertir l'objet en tableau pour l'affichage (on garde maintenant les stocks à zéro)
            const stocksArray = Object.values(latestStockByLegume);
            setConsolidatedStocks(stocksArray);

            // Calculer les légumes disponibles pour ajout (non présents dans le stock)
            if (app.legumes_nomenclature_stock && Array.isArray(app.legumes_nomenclature_stock)) {
              const stockLegumeIds = stocksArray.map(item => parseInt(item.legume_id))
              const availableLegumesArray = app.legumes_nomenclature_stock.filter(
                legume => !stockLegumeIds.includes(parseInt(legume.id))
              )
              setAvailableLegumes(availableLegumesArray)
            }
          }
          setLoading(false)
        } else {
          setApiError(true)
          setLoading(false)
        }
      })
      .catch(function (error) {
        console.error('Erreur API:', error)
        setApiError(true)
        setLoading(false)
      })
  }, [app.profil, app.legumes_nomenclature_stock])

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="w-full py-5 border-gray-200">
        <Subtitle title="Stocks" />
        <Title title="État des stocks" />

        <div className="flex justify-between my-4">
          <div>
            {availableLegumes.length > 0 && (
              <button
                onClick={() => handleStockAction(null, 'create')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-price hover:bg-price-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-price"
              >
                <FiPlusCircle className="mr-2" /> Ajouter un nouveau légume
              </button>
            )}
          </div>
          <div>
            <Link
              to="/stocks"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Voir l'historique
            </Link>
          </div>
        </div>

        {apiError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiX className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Une erreur est survenue lors du chargement des données. Veuillez réessayer plus tard.
                </p>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Oval color="#0F5F3F" height={40} width={40} />
            <span className="ml-2">Chargement...</span>
          </div>
        ) : consolidatedStocks.length === 0 ? (
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
            <p className="text-gray-500">Aucun stock déclaré</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6">
            <p className="text-xs text-gray-500 mb-6">Cliquez directement sur la quantité pour la modifier</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {consolidatedStocks.map((item) => (
                <div key={item.legume_id} className="bg-gray-50 rounded-lg p-4 flex flex-col items-center h-80 relative">
                  {/* Icône pour remettre à zéro */}
                  <button
                    onClick={() => deleteLegumeFromStock(item)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500 focus:outline-none"
                    title="Remettre à zéro le stock de ce légume"
                  >
                    <FiTrash2 size={16} />
                  </button>
                  <div className="text-sm font-medium text-gray-900 mb-2 h-16 flex items-center justify-center text-center">{item.legume_nom}</div>

                  {/* Visualisation en silo */}
                  <div
                    className="w-24 h-48 bg-gray-200 rounded-lg overflow-hidden relative mb-3 cursor-pointer"
                    onClick={() => editingItem !== item.legume_id && startEditing(item)}
                  >
                    <div
                      className="absolute bottom-0 w-full bg-price transition-all duration-500 ease-in-out"
                      style={{
                        height: `${getFillingLevel(editingItem === item.legume_id ? editValue : item.quantite, item.legume_id)}%`,
                      }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      {editingItem === item.legume_id ? (
                        <div className="bg-white/90 p-2 rounded shadow-md w-20">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-full text-center border border-gray-300 rounded p-1 text-sm"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-800 bg-white/70 px-2 py-1 rounded">
                          {item.quantite}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 h-8 flex items-center justify-center">
                    {getLegumeMax(item.legume_id) && (
                      <span className="ml-2 text-xs text-gray-400">
                        Sur {getLegumeMax(item.legume_id)} {getLegumeDetails(item.legume_id)?.unite || item.unite}s
                      </span>
                    )}

                  </div>

                  {/* Bouton d'action (validation/annulation en mode édition) */}
                  {editingItem === item.legume_id && (
                    <div className="mt-3 w-full">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => submitEdit(item)}
                          disabled={submitting}
                          className="flex-1 flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          {submitting ? (
                            <Oval color="#ffffff" height={16} width={16} />
                          ) : (
                            <>
                              <BsCheck className="mr-1" /> Valider
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelEditing}
                          disabled={submitting}
                          className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-price"
                        >
                          <FiX className="mr-1" /> Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StocksStatus
